import { useSetState } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import type { HeaderPageProps } from './headerPage.types';

/**
 * Backs the support request details header with its scroll and re-open state. Registers a window scroll listener that toggles isScroll around a scrollY of zero and removes it on cleanup, and exposes a re-open handler wrapping the handleReOpen prop in an isSavingButton flag.
 *
 * @param handleReOpen - Prop invoked to re-open the displayed support request.
 * @returns Header state together with the re-open click handler.
 */
export const useHeaderPage = ({ handleReOpen }: Pick<HeaderPageProps, 'handleReOpen'>) => {
  const [state, setState] = useSetState({
    isScroll: false,
    isVisibleFacilityInfoDrawer: false,
    isSavingButton: false,
  });

  /**
   * Runs the re-open request for the displayed support ticket. Raises isSavingButton, awaits the handleReOpen prop and lowers the flag afterwards.
   *
   * @returns Promise resolving once re-opening finished and the saving flag was cleared.
   */
  const onClickReOpen = async () => {
    setState({ isSavingButton: true });
    await handleReOpen();
    setState({ isSavingButton: false });
  };

  useEffect(() => {
    /**
     * Tracks whether the support request details page has been scrolled away from the top. Sets isScroll to true once window.scrollY is non-zero and back to false when it returns to zero.
     *
     * @returns Nothing.
     */
    const handleScroll = () => {
      if (window.scrollY === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (window.scrollY !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.isScroll]);

  return { state, onClickReOpen };
};
